import { useEffect, useState } from 'react';
import { Box, Flex, TextProps } from '@chakra-ui/react';
import { useValidateReset, useSignIn, useRecoverPassword } from 'hooks';
import RecoverPasswordForm, {
  RecoverPasswordFormProps,
  RecoverPasswordFormValues,
} from 'components/RecoverPassword/RecoverPasswordForm';
import { Spinner, Button, Text, Link } from 'components/UI';
import { checkIfError } from 'utils/helpers';
import AppRoutes from 'constants/routes';
import { errorMessagePasswordResetNotFound } from 'constants/validation';
import { IUserResourceWithRelations } from '../../../server/types';

export type RecoverPasswordQueryParams = {
  token?: string;
  referrerUrl?: string;
};

type RecoverPasswordProps = {
  onRecover?: () => void;
  onReturn?: () => void;
  formProps?: Omit<RecoverPasswordFormProps, 'onSubmit'>;
  queryParams?: RecoverPasswordQueryParams;
  requestProps?: TextProps;
};

const RecoverPassword = ({
  queryParams,
  onRecover,
  onReturn,
  formProps = {},
  requestProps = {},
}: RecoverPasswordProps) => {
  const [user, setUser] = useState<IUserResourceWithRelations>();
  const [validateReset, { loading: validateLoading, error: validateError }] = useValidateReset();
  const [recoverPassword, { error: recoverPasswordError }] = useRecoverPassword();

  const renderLink = onReturn ? (
    <Button variant="link" onClick={onReturn}>
      here
    </Button>
  ) : (
    <Link to={AppRoutes.ForgotPassword}>here</Link>
  );

  const [signIn, { error: loginError }] = useSignIn();

  const handleRecoverPassword = async (values: RecoverPasswordFormValues) => {
    if (!user?.email || !queryParams?.token) return;

    const { email } = user;

    const recoverResponse = await recoverPassword({
      token: queryParams?.token,
      password: values.password,
    });

    if (!checkIfError(recoverResponse)) {
      await signIn(email, values.password);
    }

    if (onRecover) {
      onRecover();
    }
  };

  const handleValidateReset = async (token: string) => {
    const response = await validateReset(token);

    if (checkIfError(response)) return;

    setUser(response as IUserResourceWithRelations);
  };

  useEffect(() => {
    if (!queryParams?.token) return;

    handleValidateReset(queryParams.token);
  }, [queryParams?.token]);

  if (validateLoading)
    return (
      <Flex data-testid="loader" justifyContent="center" alignItems="center" py={12}>
        <Spinner size="lg" thickness="4px" />
      </Flex>
    );

  const renderError = recoverPasswordError ?? loginError ?? (
    <Box>
      <Text mb={2}>{validateError ?? errorMessagePasswordResetNotFound}</Text>
      <Text>Please click {renderLink} to request another password reset.</Text>
    </Box>
  );

  if (validateError || recoverPasswordError || loginError || !queryParams?.token)
    return (
      <Box py={4} textAlign="center" {...requestProps}>
        {renderError}
      </Box>
    );

  return <RecoverPasswordForm onSubmit={handleRecoverPassword} {...formProps} />;
};

export default RecoverPassword;
